import {
  Navigate, Route, Outlet, Routes, 
} from 'react-router-dom';
import { FC } from 'react';

import Home from 'features/Home/Home';
import Login from 'features/Auth/Login';
import AuthLayout from 'components/Layouts/AuthLayout';
import LoginAll from 'features/Auth/LoginAll';
import LoginOmya from 'features/Auth/LoginOmya';
import LoginSetPassword from 'features/Auth/LoginSetPassword';
import LoginExpired from 'features/Auth/LoginExpired';
import Profile from 'features/Profile';
import Help from 'features/Help';
import ProductConverter from 'features/ProductConverter';
import ApplicationBrief from 'features/ApplicationBrief';
import ProspectMap from 'features/ProspectMap';
import ProspectMapOffline from 'features/ProspectMapOffline';
import Croptimizer from 'features/Croptimizer';
import ProductFinder from 'features/ProductFinder';
import FeasibilityCheck from 'features/FeasibilityCheck';
import SetPhoneNumber from 'features/Auth/SetPhoneNumber';
import ConfirmCode from 'features/Auth/ConfirmCode';
import TermsOfUse from 'features/TermsOfUse';
import PrivacyPolicy from 'features/PrivacyPolicy';
import useUser from 'api/hooks/useUser';
import { RoleNames } from 'utils/constants';
import SecureStorage from 'utils/localStorage';

import useToken from './hooks/useToken';

interface ICustomRoute {
  typeUser?: Array<'All' | 'Blue' | 'Green' | 'Lightblue'>;
}

const PrivateRoute: FC<ICustomRoute> = ({ typeUser = 'All' }) => {
  const mfa = SecureStorage.getItemSync('mfa');
  const token = SecureStorage.getItemSync('token');
  const ssoLogin = SecureStorage.getItemSync('ssoLogin');
  const { data: user, isPending } = useUser();

  if (isPending) {
    return null;
  }

  const isAdmin = user?.role?.name === RoleNames.admin;
  const isBlueGreen = user?.role?.name === RoleNames.blueGreen;

  if (!token || !user) {
    return <Navigate to="/auth/login-all" replace />;
  }

  const isOmyaUser = user.category === 'Omya User';
  const skipMfa = isOmyaUser || !!ssoLogin;

  if (!skipMfa && !user?.phoneNumber) {
    return <Navigate to="/auth/set-phone-number" replace />;
  }

  if (token && user?.phoneNumber && !mfa && !skipMfa) {
    return <Navigate to="/auth/confirm-mfa" replace />;
  }

  if (
    !isAdmin &&
    !isBlueGreen &&
    !typeUser.includes(RoleNames.all) &&
    !typeUser.includes(user?.role?.name)
  ) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

const TokenRoute: FC<ICustomRoute> = () => {
  const token = SecureStorage.getItemSync('token');

  if (!token) {
    return <Navigate to="/auth/login-all" replace />;
  }

  return <Outlet />;
};

const Navigation = () => {
  const { loading } = useToken();

  if (loading) {
    return null;
  }

  return (
    <Routes>
      <Route path="/" element={<PrivateRoute />}>
        <Route path="/" element={<Home />} />
      </Route>
      <Route path="/profile" element={<PrivateRoute />}>
        <Route path="/profile" element={<Profile />} />
      </Route>
      <Route path="/help" element={<PrivateRoute />}>
        <Route path="/help" element={<Help />} />
      </Route>
      <Route
        path="/product-converter"
        element={
          <PrivateRoute typeUser={[RoleNames.blue, RoleNames.blueLight]} />
        }
      >
        <Route path="/product-converter" element={<ProductConverter />} />
      </Route>
      <Route
        path="/application-brief"
        element={
          <PrivateRoute typeUser={[RoleNames.blue, RoleNames.blueLight]} />
        }
      >
        <Route path="/application-brief" element={<ApplicationBrief />} />
      </Route>
      <Route
        path="/prospect-map"
        element={<PrivateRoute typeUser={[RoleNames.blue]} />}
      >
        <Route path="/prospect-map" element={<ProspectMap />} />
      </Route>
      <Route
        path="/prospect-map-offline"
        element={<PrivateRoute typeUser={[RoleNames.blue]} />}
      >
        <Route path="/prospect-map-offline" element={<ProspectMapOffline />} />
      </Route>
      <Route
        path="/product-finder"
        element={<PrivateRoute typeUser={[RoleNames.blue]} />}
      >
        <Route path="/product-finder" element={<ProductFinder />} />
      </Route>
      <Route
        path="/feasibility-check"
        element={
          <PrivateRoute typeUser={[RoleNames.blue, RoleNames.blueLight]} />
        }
      >
        <Route path="/feasibility-check" element={<FeasibilityCheck />} />
      </Route>
      <Route
        path="/croptimizer"
        element={<PrivateRoute typeUser={[RoleNames.green]} />}
      >
        <Route path="/croptimizer" element={<Croptimizer />} />
      </Route>

      <Route path="/auth" element={<AuthLayout />}>
        <Route path="/auth" element={<Navigate to="login-all" replace />} />
        <Route path="login-distributor" element={<Login />} />
        <Route path="login-all" element={<LoginAll />} />
        <Route path="login-omya" element={<LoginOmya />} />
        <Route path="microsoft-mobile" element={<LoginOmya />} />
        <Route path="login-set-password" element={<LoginSetPassword />} />
        <Route path="login-expired" element={<LoginExpired />} />
        <Route path="set-phone-number" element={<TokenRoute />}>
          <Route path="/auth/set-phone-number" element={<SetPhoneNumber />} />
        </Route>
        <Route path="confirm-phone-number" element={<TokenRoute />}>
          <Route
            path="/auth/confirm-phone-number"
            element={<ConfirmCode type="phoneNumber" />}
          />
        </Route>
        <Route path="confirm-mfa" element={<TokenRoute />}>
          <Route
            path="/auth/confirm-mfa"
            element={<ConfirmCode type="mfa" />}
          />
        </Route>
      </Route>
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default Navigation;
